import React, { useEffect, useRef, useState } from 'react'
import Button from 'components/button'
import { ArrowUp, Attachment } from 'components/icons'
import { CHAT_FILE_ACCEPT } from 'components/chat/files'
import PageSkeleton from './skeleton'

const PLACEHOLDER_START = 'Ask MortaAI to build '
const TYPE_MS = 40
const HOLD_MS = 2600
const REDUCED_MOTION_MS = 3500

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false

/** Types each idea out after "Ask MortaAI to build ", holds it, then moves on; whole ideas when motion is reduced. */
function useTypedPlaceholder(ideas: string[]): string {
  const [reduced] = useState(prefersReducedMotion)
  const [index, setIndex] = useState(0)
  const [typed, setTyped] = useState(reduced ? Infinity : 0)
  const idea = ideas[index % ideas.length] ?? ''
  const done = typed >= idea.length

  useEffect(() => {
    if (ideas.length === 0) return
    const timeout = window.setTimeout(
      () => {
        if (!done) return setTyped((count) => count + 1)
        setIndex((current) => current + 1)
        setTyped(reduced ? Infinity : 0)
      },
      reduced ? REDUCED_MOTION_MS : done ? HOLD_MS : TYPE_MS
    )
    return () => window.clearTimeout(timeout)
  }, [ideas.length, done, typed, reduced])

  return PLACEHOLDER_START + (done ? `${idea}…` : idea.slice(0, typed))
}

const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** The text with each whole, exactly-cased occurrence of a name in semibold, longest names first. */
function emphasiseNames(text: string, names: string[]): React.ReactNode {
  const unique = [...new Set(names.map((name) => name.trim()).filter(Boolean))].sort((a, b) => b.length - a.length)
  if (unique.length === 0) return text
  const pattern = new RegExp(`(?<![\\w])(${unique.map(escapeRegExp).join('|')})(?![\\w])`, 'g')
  return text.split(pattern).map((part, index) =>
    // split() with one capture group puts the matched names at the odd indexes.
    index % 2 === 1 ? (
      <span key={index} className="font-semibold">
        {part}
      </span>
    ) : (
      part
    )
  )
}

// Soft light from the page's foot, in Morta blue alone.
const BACKDROP = [
  'radial-gradient(60% 55% at 18% 95%, color-mix(in srgb, var(--color-blue) 22%, transparent), transparent 70%)',
  'radial-gradient(55% 50% at 85% 92%, color-mix(in srgb, var(--color-light-blue) 70%, transparent), transparent 70%)',
  'radial-gradient(70% 60% at 50% 108%, color-mix(in srgb, var(--color-blue) 38%, transparent), transparent 72%)'
].join(', ')

// The composer's buttons, as in the MortaAI panel: a round outlined attach on the left, a round blue send on the right.
const ATTACH_BUTTON =
  'w-8 h-8 rounded-full p-0 inline-flex items-center justify-center bg-surface border border-divider text-muted cursor-pointer shrink-0 transition-colors hover:text-body hover:bg-surface-sunken'
const SEND_BUTTON =
  'w-8 h-8 rounded-full p-0 inline-flex items-center justify-center bg-blue border-0 shadow-none cursor-pointer text-on-solid shrink-0 transition-opacity hover:enabled:opacity-90 disabled:bg-surface-strong disabled:text-muted disabled:opacity-100'

interface CustomViewPromptProps {
  tableName: string
  /** What the placeholder types out and the chips offer, each finishing "Ask MortaAI to build …". */
  ideas: string[]
  /** The table's and its columns' names, which the ideas show in semibold so they read as names. */
  names?: string[]
  onSubmit: (prompt: string, files?: File[]) => void
}

/** A new Custom View's first screen: one prompt, front and centre, that hands the page to MortaAI. */
export default function CustomViewPrompt({ tableName, ideas, names = [], onSubmit }: CustomViewPromptProps) {
  const [value, setValue] = useState('')
  const fileInput = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const placeholder = useTypedPlaceholder(ideas)
  const text = value.trim()

  // Files go with the prompt only when some are attached.
  const ask = (prompt: string) => (files.length ? onSubmit(prompt, files) : onSubmit(prompt))

  const submit = (event?: React.FormEvent) => {
    event?.preventDefault()
    if (text) ask(text)
  }

  return (
    <section
      className="relative flex flex-1 min-h-0 items-center justify-center overflow-y-auto overflow-x-hidden bg-surface px-4 py-12"
      data-testid="custom-view-prompt"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-[10%] animate-hero-drift motion-reduce:animate-none"
        style={{ backgroundImage: BACKDROP }}
      />
      <div className="relative flex w-full max-w-[640px] flex-col items-center text-center animate-slide-in">
        <h2 className="text-3xl sm:text-4xl font-normal leading-tight tracking-tight text-body text-balance">
          Ask <span className="font-bold text-blue">MortaAI</span> to build something with{' '}
          {tableName ? (
            <>
              <span className="font-bold">{tableName}</span>&apos;s data
            </>
          ) : (
            'this table\u2019s data'
          )}
        </h2>

        <form
          onSubmit={submit}
          className="mt-8 w-full rounded-2xl border border-divider bg-surface p-2 text-left shadow-[0_12px_32px_-16px_rgba(46,127,249,0.45)] transition-shadow focus-within:ring-2 focus-within:ring-blue/20"
        >
          {files.length > 0 && (
            <div className="flex flex-wrap gap-1.5 px-1 pt-1 pb-1.5">
              {files.map((file, index) => (
                <span
                  key={`${file.name}-${file.size}-${file.lastModified}`}
                  className="inline-flex max-w-[200px] items-center gap-1.5 rounded-lg border border-divider bg-surface-sunken px-2 py-1 text-xs text-body"
                >
                  <span className="truncate">{file.name}</span>
                  <Button
                    internalType="transparent"
                    aria-label={`Remove ${file.name}`}
                    className="h-4 w-4 p-0 text-[10px] text-muted hover:bg-transparent hover:text-red"
                    onClick={() => setFiles((previous) => previous.filter((_, i) => i !== index))}
                  >
                    ✕
                  </Button>
                </span>
              ))}
            </div>
          )}
          <textarea
            // Styles itself, so the app-wide text-field rules (a grey wash on hover) leave it alone.
            data-slot="textarea"
            aria-label="Describe what to build"
            // The first thing a new view asks for, so it takes focus straight away.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            rows={3}
            value={value}
            placeholder={placeholder}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || event.shiftKey) return
              event.preventDefault()
              submit()
            }}
            className="block w-full max-h-48 resize-none border-0 bg-transparent px-2 pt-1.5 pb-1 [font-family:inherit] text-sm leading-6 text-body outline-none shadow-none placeholder:text-muted"
          />
          <div className="flex items-center justify-between pt-1">
            <input
              ref={fileInput}
              id="custom-view-prompt-files"
              type="file"
              multiple
              accept={CHAT_FILE_ACCEPT}
              className="absolute h-0 w-0 overflow-hidden opacity-0 pointer-events-none"
              onChange={(event) => {
                const added = Array.from(event.target.files || [])
                if (added.length) setFiles((previous) => [...previous, ...added])
                event.target.value = ''
              }}
            />
            {/* A button rather than a label for the hidden input, so the keyboard reaches it. */}
            <Button
              internalType="outline"
              aria-label="Attach file"
              className={ATTACH_BUTTON}
              onClick={() => fileInput.current?.click()}
            >
              <Attachment />
            </Button>
            <Button type="submit" aria-label="Build with MortaAI" disabled={!text} className={SEND_BUTTON}>
              <ArrowUp />
            </Button>
          </div>
        </form>

        {ideas.length > 0 && (
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {ideas.map((idea) => (
              <Button
                key={idea}
                internalType="outline"
                className="h-7 rounded-full border-divider bg-surface px-3 text-xs font-normal text-body hover:bg-surface-sunken"
                onClick={() => ask(`Build ${idea}`)}
              >
                {/* One child, so the button's gap doesn't open up around the names. */}
                <span>{emphasiseNames(capitalise(idea), names)}</span>
              </Button>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

/**
 * What a new Custom View shows while MortaAI builds the first page asked for: a page of its kind still
 * loading, in the Morta look. MortaAI's progress is in its panel beside it.
 */
export function CustomViewBuilding() {
  return <PageSkeleton testId="custom-view-building" label="MortaAI is building your view" />
}
